/**
 * Service for handling audio alerts
 * Provides alarming siren sound when phases expire
 */
export class AudioService {
    constructor() {
        this.audioContext = null;
        this.initialized = false;
        this.initializeAudio();
    }

    /**
     * Initialize Web Audio API
     */
    initializeAudio() {
        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            this.initialized = true;
        } catch (error) {
            console.warn('Web Audio API not supported:', error);
        }
    }

    /**
     * Play an alarming siren sound
     */
    async playSiren() {
        if (!this.initialized || !this.audioContext) return;

        try {
            // Resume audio context if suspended (browser policy)
            if (this.audioContext.state === 'suspended') {
                await this.audioContext.resume();
            }

            this.createSirenSound();
        } catch (error) {
            console.error('Error playing siren:', error);
        }
    }

    /**
     * Create a siren-like sound using oscillators
     */
    createSirenSound() {
        const duration = 2.0; // 2 seconds
        const now = this.audioContext.currentTime;
        
        // Create two oscillators for the siren effect
        const osc1 = this.audioContext.createOscillator();
        const osc2 = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();
        
        // Connect oscillators to gain node
        osc1.connect(gainNode);
        osc2.connect(gainNode);
        gainNode.connect(this.audioContext.destination);
        
        // Configure oscillators for siren effect
        osc1.type = 'sine';
        osc2.type = 'sine';
        
        // Create siren frequency sweep
        const startFreq1 = 800;  // Start frequency
        const endFreq1 = 1200;   // End frequency
        const startFreq2 = 400;  // Lower harmonic
        const endFreq2 = 600;    // Lower harmonic end
        
        // First oscillator - main siren
        osc1.frequency.setValueAtTime(startFreq1, now);
        osc1.frequency.linearRampToValueAtTime(endFreq1, now + duration / 2);
        osc1.frequency.linearRampToValueAtTime(startFreq1, now + duration);
        
        // Second oscillator - harmonic
        osc2.frequency.setValueAtTime(startFreq2, now);
        osc2.frequency.linearRampToValueAtTime(endFreq2, now + duration / 2);
        osc2.frequency.linearRampToValueAtTime(startFreq2, now + duration);
        
        // Volume envelope for dramatic effect
        gainNode.gain.setValueAtTime(0, now);
        gainNode.gain.linearRampToValueAtTime(0.3, now + 0.1);
        gainNode.gain.setValueAtTime(0.3, now + duration - 0.1);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + duration);
        
        // Start and stop oscillators
        osc1.start(now);
        osc1.stop(now + duration);
        osc2.start(now);
        osc2.stop(now + duration);
        
        // Add pulsing effect by modulating the gain
        this.addPulsingEffect(gainNode, now, duration);
    }

    /**
     * Add pulsing effect to the siren
     * @param {GainNode} gainNode - The gain node to modulate
     * @param {number} startTime - When to start the effect
     * @param {number} duration - How long the effect lasts
     */
    addPulsingEffect(gainNode, startTime, duration) {
        const lfo = this.audioContext.createOscillator();
        const lfoGain = this.audioContext.createGain();
        
        lfo.frequency.setValueAtTime(8, startTime); // 8 Hz pulsing
        lfo.type = 'sine';
        
        lfoGain.gain.setValueAtTime(0.1, startTime);
        
        lfo.connect(lfoGain);
        lfoGain.connect(gainNode.gain);
        
        lfo.start(startTime);
        lfo.stop(startTime + duration);
    }
}